import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";

interface FaqItem {
  q: string;
  a: string;
}
interface FaqGroup {
  title: string;
  icon: string;
  color: string;
  items: FaqItem[];
}

const faqGroups: FaqGroup[] = [
  {
    title: "收藏与书架",
    icon: "ri-bookmark-3-line",
    color: "bg-primary-100 text-primary-800",
    items: [
      {
        q: "怎么把喜欢的漫画加入书架收藏？",
        a: "打开任意漫画的详情页，点击「加入书架」按钮即可一键收藏。收藏成功后，可以在书架里集中查看和管理你所有追更的作品。",
      },
      {
        q: "书架里的漫画可以分类整理吗？",
        a: "可以。书架支持按分类、作者、更新状态等维度智能分组，你也可以手动创建自定义分类，把作品整理得井井有条。",
      },
      {
        q: "收藏夹会多设备同步吗？",
        a: "会的。收藏与账号绑定，只要登录同一个账号，手机、平板、电脑上的书架内容都会实时同步。",
      },
    ],
  },
  {
    title: "更新订阅",
    icon: "ri-notification-3-line",
    color: "bg-accent-100 text-accent-800",
    items: [
      {
        q: "订阅更新后会如何提醒我？",
        a: "你订阅的漫画一旦更新，首页的「追更提醒」和个人中心都会第一时间出现提醒，让你不错过任何一话。",
      },
      {
        q: "可以取消订阅吗？",
        a: "随时可以。在订阅列表或漫画详情页一键取消订阅即可，取消后不再接收该作品的更新提醒。",
      },
    ],
  },
  {
    title: "离线缓存",
    icon: "ri-download-cloud-2-line",
    color: "bg-secondary-100 text-secondary-800",
    items: [
      {
        q: "如何把漫画下载到本地离线观看？",
        a: "在漫画详情页或章节列表点击「离线缓存」，即可按章节或整部下载。缓存成功后，没有网络也能随时阅读。",
      },
      {
        q: "离线缓存会占用很多存储空间吗？",
        a: "可以灵活控制。你可以选择缓存的范围，也能随时在设置里一键清理不再需要的缓存，空间占用完全由你掌控。",
      },
    ],
  },
  {
    title: "历史记录",
    icon: "ri-history-line",
    color: "bg-primary-100 text-primary-800",
    items: [
      {
        q: "阅读历史会自动保存吗？",
        a: "会。你阅读过的每一部漫画和每一话进度都会自动记录，方便你随时从上次停下的地方继续阅读。",
      },
      {
        q: "换了设备还能接着看吗？",
        a: "能。历史记录与账号绑定并云端同步，在任意设备登录后都能看到完整的历史记录并继续阅读。",
      },
    ],
  },
  {
    title: "个性化推荐",
    icon: "ri-star-smile-line",
    color: "bg-accent-100 text-accent-800",
    items: [
      {
        q: "个性化推荐是怎么工作的？",
        a: "系统会结合你的收藏、订阅和阅读行为，分析你的喜好，为你推荐可能感兴趣的新番与宝藏作品，并随你的使用不断优化。",
      },
      {
        q: "我对推荐结果不满意，能调整吗？",
        a: "可以。你可以对每一条推荐直接点「感兴趣」或「不感兴趣」，也可以多收藏订阅喜欢的作品，让推荐越来越懂你。",
      },
    ],
  },
  {
    title: "账号与其他",
    icon: "ri-user-settings-line",
    color: "bg-secondary-100 text-secondary-800",
    items: [
      {
        q: "收藏、订阅、离线这些功能需要付费吗？",
        a: "这些均为基础功能，注册登录后即可免费使用。部分额外权益，如更高的离线缓存额度与去广告，可能提供付费会员，具体以页面说明为准。",
      },
      {
        q: "在哪里可以联系到客服？",
        a: "你可以在「关于我们」页面的联系方式中找到我们的邮箱与在线客服，工作日 9:00-21:00 都会及时响应你的问题。",
      },
    ],
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqGroups.flatMap((g) =>
    g.items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  ),
};

export default function Faq() {
  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <section className="bg-background-100 py-8">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">常见问题</span>
          </nav>
          <div className="mt-4 max-w-2xl">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">常见问题</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              关于收藏、订阅、离线、历史记录、个性化推荐与账号的常见疑问，都在这里。若未找到答案，欢迎联系我们。
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* 分类目录 */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="font-heading text-lg text-foreground-950">问题分类</h2>
              <ul className="mt-4 flex flex-col gap-1.5">
                {faqGroups.map((g) => (
                  <li key={g.title}>
                    <a
                      href={`#faq-${g.title}`}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 transition hover:bg-primary-100 hover:text-primary-800"
                    >
                      <i className={`${g.icon} text-base ${g.color.split(" ")[1]}`}></i>
                      {g.title}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-md bg-background-50 p-4">
                <p className="text-xs text-foreground-500">没找到想要的问题？</p>
                <Link
                  to="/about"
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
                >
                  联系我们 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </aside>

          {/* 问题内容 */}
          <div className="lg:col-span-2">
            <div className="flex flex-col gap-8">
              {faqGroups.map((g) => (
                <section key={g.title} id={`faq-${g.title}`} aria-labelledby={g.title}>
                  <div className="flex items-center gap-2.5">
                    <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${g.color}`}>
                      <i className={g.icon}></i>
                    </span>
                    <h2 className="font-heading text-xl text-foreground-950">{g.title}</h2>
                  </div>
                  <div className="mt-4 flex flex-col gap-3">
                    {g.items.map((item) => (
                      <details
                        key={item.q}
                        open
                        className="group rounded-lg border border-background-200/70 bg-background-50 p-4"
                      >
                        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-bold text-foreground-950">
                          {item.q}
                          <i className="ri-add-line text-secondary-600 transition group-open:rotate-45"></i>
                        </summary>
                        <p className="mt-3 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                      </details>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
        <div className="flex flex-col items-center justify-between gap-4 rounded-lg bg-primary-500 px-6 py-8 text-center sm:flex-row sm:text-left">
          <div>
            <h2 className="font-heading text-xl text-background-50">问题还没解决？</h2>
            <p className="mt-1 text-sm text-primary-100">联系我们，我们很乐意帮你搞定。</p>
          </div>
          <Link
            to="/about"
            className="whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-medium text-primary-900 transition hover:bg-background-100"
          >
            联系客服
          </Link>
        </div>
      </section>
    </div>
  );
}